import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowDownToLine,
  Banknote,
  ChevronDown,
  ChevronRight,
  Coins,
  Download,
  Eye,
  EyeOff,
  RefreshCw,
  RotateCcw,
  Save,
  Settings as SettingsIcon,
} from 'lucide-react';

const HISTORY_CAP = 50;
import { api } from '../api/client';
import { fmtMoney } from '../lib/format';
import { computeCashflow } from '../lib/cashflow/cashflow';
import { downloadCombinedXlsx } from '../lib/combinedXlsxExport';
import { deriveBudget } from '../lib/totals';
import { parseYm } from '../lib/cashflow/timing';
import { CashflowBankPanel } from './CashflowBankPanel';
import type { BudgetData, LineItem } from '../types';
import type {
  CashflowAlert,
  CashflowData,
  CashflowMonth,
  TimingProfile,
  TimingProfileKind,
  XeroCashByMonthData,
  XeroCashByMonthRow,
  XeroMonthlyActuals,
} from '../cashflowTypes';

type Props = {
  budget: BudgetData;
  onBack: () => void;
  isViewer?: boolean;
  isRegistrar: boolean;
  onApplyToBudget?: (updated: BudgetData) => Promise<void>;
};

type Fy26ProposalLine = {
  id: string;
  code: string;
  name: string;
  current: number;
  proposed: number;
};
type Fy26Proposal = {
  baseYear: number;
  firstYm: string;
  lastYm: string;
  income: Fy26ProposalLine[];
  expenses: Fy26ProposalLine[];
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MONTH_LABEL = (ym: string): string => {
  const parts = ym.split('-');
  const y = parseInt(parts[0] ?? '0', 10);
  const m = parseInt(parts[1] ?? '1', 10);
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const KIND_OPTIONS: { value: TimingProfileKind; label: string }[] = [
  { value: 'historical', label: 'Historical Weight (default)' },
  { value: 'monthly', label: 'Monthly (1/12 each)' },
  { value: 'quarterly', label: 'Quarterly (Jan/Apr/Jul/Oct)' },
  { value: 'annual', label: 'Annual (one month)' },
  { value: 'custom', label: 'Custom' },
];

function defaultProfileFor(
  lineId: string,
  displayOrder = 0,
  /** Xero TaxType-derived GST status. Used as the default for new
   *  profiles so creating a profile doesn't silently override Xero. */
  xeroHasGst = true,
): TimingProfile {
  return {
    lineId,
    // Default = historical weights from sheet data. Departing from
    // history is a deliberate user override, not the implicit baseline.
    kind: 'historical',
    anchorMonths: [],
    customMonths: null,
    gstApplicable: xeroHasGst,
    excluded: false,
    displayOrder,
    amountOverrides: null,
  };
}

// ─── EditableCell ─────────────────────────────────────────────────────────────

function EditableCell({
  value,
  ym,
  hasOverride,
  isRegistrar,
  onCommit,
}: {
  value: number;
  ym: string;
  hasOverride: boolean;
  isRegistrar: boolean;
  onCommit: (v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');

  const startEdit = () => {
    if (!isRegistrar) return;
    setRaw(value !== 0 ? String(Math.round(value * 100) / 100) : '');
    setEditing(true);
  };

  const commit = () => {
    const trimmed = raw.trim().replace(/[$,\s]/g, '');
    if (trimmed === '') {
      onCommit(null);
    } else {
      const n = parseFloat(trimmed);
      if (!isNaN(n)) onCommit(n);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after an explicit click-to-edit action
        autoFocus
        type="text"
        inputMode="decimal"
        className="w-16 text-right num text-[11px] bg-blue-50 border-b border-ccaanz-blue outline-none px-1"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={commit}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <span
      className={[
        'block text-right select-none',
        isRegistrar ? 'cursor-pointer hover:bg-ccaanz-blue/5 rounded' : '',
        hasOverride ? 'text-ccaanz-blue font-medium underline decoration-dotted' : '',
        !value ? 'text-ccaanz-grey/30' : '',
      ].join(' ')}
      role="button"
      tabIndex={0}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEdit();
        }
      }}
      title={
        hasOverride
          ? `Override active for ${ym} — click to edit, clear to reset`
          : isRegistrar
            ? `Click to override ${ym}`
            : undefined
      }
    >
      {value ? fmtMoney(value) : isRegistrar ? '—' : ''}
    </span>
  );
}

// ─── SheetLineRow ─────────────────────────────────────────────────────────────

type SheetLineRowProps = {
  code: string;
  name: string;
  /** True when this GL code is linked to a cashflow projection line (budget or future mapping). */
  hasBudgetLink: boolean;
  /** Xero GL Cache TaxType says this line is GST-bearing. Used as the
   *  authoritative GST flag unless the user has set an explicit override
   *  in the timing profile. */
  xeroHasGst: boolean;
  monthValues: number[];
  monthYms: string[];
  /** YMs that are projected (not finalised actuals). Only projected months are editable. */
  projectedYms: Set<string>;
  profile: TimingProfile | null;
  onProfileChange: (patch: Partial<TimingProfile>) => void;
  isRegistrar: boolean;
};

function SheetLineRow({
  code,
  name,
  hasBudgetLink,
  xeroHasGst,
  monthValues,
  monthYms,
  projectedYms,
  profile,
  onProfileChange,
  isRegistrar,
}: SheetLineRowProps) {
  const [open, setOpen] = useState(false);
  const excluded = profile?.excluded ?? false;
  const overrideCount = profile?.amountOverrides ? Object.keys(profile.amountOverrides).length : 0;
  // Effective GST flag — explicit profile wins, else Xero TaxType.
  const gstEffective = profile?.gstApplicable ?? xeroHasGst;
  const gstIsOverride = profile != null && profile.gstApplicable !== xeroHasGst;
  // Timing departs from the historical default when the user has saved
  // any kind other than 'historical'. Highlights the GL code so you can
  // spot manual overrides at a glance.
  const timingIsOverride = profile != null && profile.kind !== 'historical';
  // Show the GL code highlight for ANY active override — timing kind OR
  // per-month amount overrides. This ensures the indicator survives if the
  // user later resets the kind back to 'historical' while cell overrides remain.
  const hasAnyOverride = timingIsOverride || overrideCount > 0;

  return (
    <>
      <tr className="row-data">
        <td className="sticky left-0 bg-[var(--white)] !py-1">
          <div className="flex items-center gap-0.5 pr-1" style={{ minWidth: 220 }}>
            {hasBudgetLink ? (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={[
                  'text-left hover:text-ccaanz-blue flex-1 truncate flex items-center gap-1',
                  excluded ? 'line-through text-[var(--text2)]' : '',
                ].join(' ')}
                title="Edit timing / overrides for this line"
              >
                <span
                  className={[
                    'line-code shrink-0',
                    hasAnyOverride ? 'bg-[var(--brand-lt)] px-1 rounded' : '',
                  ].join(' ')}
                  style={{ fontSize: '11px' }}
                  title={
                    timingIsOverride
                      ? `Timing manually set to "${profile?.kind}" — differs from historical default`
                      : overrideCount > 0
                        ? `${overrideCount} month override${overrideCount !== 1 ? 's' : ''} active`
                        : undefined
                  }
                >
                  {code}
                </span>
                {name}
              </button>
            ) : (
              <span
                className={[
                  'flex-1 truncate flex items-center gap-1',
                  excluded ? 'line-through text-[var(--text2)]' : 'text-ccaanz-grey/70',
                ].join(' ')}
                title="No cashflow projection — add this GL code to the budget to enable projections"
              >
                <span
                  className={[
                    'line-code shrink-0',
                    hasAnyOverride ? 'bg-[var(--brand-lt)] px-1 rounded' : '',
                  ].join(' ')}
                  style={{ fontSize: '11px' }}
                  title={
                    timingIsOverride
                      ? `Timing manually set to "${profile?.kind}" — differs from historical default`
                      : overrideCount > 0
                        ? `${overrideCount} month override${overrideCount !== 1 ? 's' : ''} active`
                        : undefined
                  }
                >
                  {code}
                </span>
                {name}
              </span>
            )}
            {gstEffective && (
              <span
                className={[
                  'shrink-0 text-[10px] font-bold px-1.5 py-px rounded leading-tight border',
                  gstIsOverride
                    ? 'bg-[var(--amber)]/15 text-[var(--amber)] border-[var(--amber)]/40'
                    : 'bg-[var(--blue)]/10 text-[var(--blue)] border-[var(--blue)]/30',
                ].join(' ')}
                title={
                  gstIsOverride
                    ? 'GST-applicable (manual override; Xero says ' +
                      (xeroHasGst ? 'yes' : 'no') +
                      ')'
                    : 'GST-applicable per Xero TaxType'
                }
              >
                GST
              </span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (isRegistrar) onProfileChange({ excluded: !excluded });
              }}
              className={[
                'row-control shrink-0 p-0.5 rounded transition-colors',
                excluded
                  ? 'text-[var(--amber)] bg-[var(--amber)]/10 hover:bg-[var(--amber)]/20'
                  : 'text-ccaanz-grey/70 hover:text-ccaanz-blue hover:bg-[var(--blue-lt)]',
                !isRegistrar ? 'pointer-events-none' : '',
              ].join(' ')}
              title={
                !isRegistrar
                  ? undefined
                  : excluded
                    ? 'Excluded from totals — click to include again'
                    : 'Click to exclude from totals'
              }
            >
              {excluded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </td>
        {monthValues.map((v, i) => (
          <td key={monthYms[i] ?? i} className="text-right !py-1">
            <EditableCell
              value={v}
              ym={monthYms[i] ?? ''}
              hasOverride={!!profile?.amountOverrides?.[monthYms[i] ?? '']}
              isRegistrar={isRegistrar && hasBudgetLink && projectedYms.has(monthYms[i] ?? '')}
              onCommit={(val) => {
                const ym = monthYms[i] ?? '';
                const { [ym]: _removed, ...rest } = profile?.amountOverrides ?? {};
                const next = val === null ? rest : { ...rest, [ym]: val };
                onProfileChange({ amountOverrides: Object.keys(next).length > 0 ? next : null });
              }}
            />
          </td>
        ))}
      </tr>

      {open && hasBudgetLink && (
        <tr>
          <td
            colSpan={monthValues.length + 1}
            className="bg-[var(--blue-lt)] px-3 py-2 border-t border-[var(--border-soft)]"
          >
            {/* Sticky-left wrapper pins the form to the visible left edge
                during horizontal scroll, same behaviour as the line-name
                column. The form stays in view no matter how far right
                the user has scrolled. */}
            <div
              className="sticky inline-flex flex-wrap gap-4 items-end text-[12px]"
              style={{ left: 12 }}
            >
              <label className="flex flex-col gap-1" style={{ minWidth: 220 }}>
                <span className="text-ccaanz-grey font-medium">Timing</span>
                <select
                  className="input"
                  style={{ minWidth: 220 }}
                  value={profile?.kind ?? 'historical'}
                  disabled={!isRegistrar}
                  onChange={(e) => onProfileChange({ kind: e.target.value as TimingProfileKind })}
                >
                  {KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              {profile?.kind === 'annual' && (
                <label className="flex flex-col gap-1" style={{ minWidth: 140 }}>
                  <span className="text-ccaanz-grey font-medium">Month</span>
                  <select
                    className="input"
                    style={{ minWidth: 140 }}
                    value={profile.anchorMonths[0] ?? 1}
                    disabled={!isRegistrar}
                    onChange={(e) =>
                      onProfileChange({ anchorMonths: [parseInt(e.target.value, 10)] })
                    }
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {profile?.kind === 'quarterly' && (
                <span className="text-ccaanz-grey self-end pb-1">
                  Default: Jan / Apr / Jul / Oct
                </span>
              )}

              <label
                className="flex items-center gap-2 self-end pb-2 whitespace-nowrap"
                style={{ minWidth: 200 }}
                title={
                  gstIsOverride
                    ? `Manual override — Xero TaxType says ${xeroHasGst ? 'GST-applicable' : 'not GST-applicable'}. Toggle to revert / change.`
                    : `Sourced from Xero TaxType (${xeroHasGst ? 'GST-applicable' : 'not GST-applicable'}). Toggle to override.`
                }
              >
                <input
                  type="checkbox"
                  checked={gstEffective}
                  disabled={!isRegistrar}
                  onChange={(e) => onProfileChange({ gstApplicable: e.target.checked })}
                  style={{ width: 16, height: 16 }}
                />
                <span>
                  GST-applicable
                  <span className="text-ccaanz-grey ml-1">
                    {gstIsOverride ? '(manual override)' : '(from Xero)'}
                  </span>
                </span>
              </label>

              {overrideCount > 0 && isRegistrar && (
                <button
                  type="button"
                  onClick={() => onProfileChange({ amountOverrides: null })}
                  className="btn btn-ghost btn-sm text-ccaanz-red self-end"
                >
                  Clear {overrideCount} override{overrideCount !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── SubtotalRow ──────────────────────────────────────────────────────────────

function SubtotalRow({
  label,
  values,
  monthYms,
  variant = 'category',
}: {
  label: string;
  values: number[];
  monthYms: string[];
  variant?: 'category' | 'section';
}) {
  return (
    <tr className={variant === 'section' ? 'row-total' : 'row-subtotal'}>
      <td className="sticky left-0 bg-[var(--paper)]">
        <span className={variant === 'category' ? 'pl-4' : ''}>{label}</span>
      </td>
      {values.map((v, i) => (
        <td key={monthYms[i]} className="text-right">
          {v ? fmtMoney(v) : ''}
        </td>
      ))}
    </tr>
  );
}

// ─── SheetSection ─────────────────────────────────────────────────────────────

type SheetSectionProps = {
  name: string;
  rows: XeroCashByMonthRow[];
  monthYms: string[];
  projection: ReturnType<typeof computeCashflow>;
  codeToLineId: Map<string, string>;
  /** Budget income line IDs — used to override row.isIncome for budget-linked rows,
   *  so section-heading mismatches in the Xero export don't misdirect amounts. */
  incomeLineIds: Set<string>;
  profileByLine: Map<string, TimingProfile>;
  onProfileChange: (lineId: string, patch: Partial<TimingProfile>) => void;
  isRegistrar: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
};

function SheetSection({
  name,
  rows,
  monthYms,
  projection,
  codeToLineId,
  incomeLineIds,
  profileByLine,
  onProfileChange,
  isRegistrar,
  isCollapsed,
  onToggleCollapse,
}: SheetSectionProps) {
  const colCount = monthYms.length + 1;

  // For budget-linked rows, trust the budget's income/expense classification over
  // the sheet's isIncome flag (which depends on Xero section headings that vary).
  const effectiveIsIncome = (row: XeroCashByMonthRow): boolean => {
    const lineId = codeToLineId.get(row.code);
    if (lineId !== undefined) return incomeLineIds.has(lineId);
    return row.isIncome;
  };

  const projectedYms = new Set(
    projection.months.filter((m) => m.status === 'projected').map((m) => m.ym),
  );

  const subtotals = projection.months.map((m) => {
    let s = 0;
    for (const row of rows) {
      const lineId = codeToLineId.get(row.code) ?? `_xero_${row.code}`;
      if (profileByLine.get(lineId)?.excluded) continue;
      const byLine = effectiveIsIncome(row) ? m.incomeByLine : m.expenseByLine;
      s += byLine[lineId] ?? 0;
    }
    return s;
  });

  return (
    <>
      <tr className="row-category cursor-pointer select-none" onClick={onToggleCollapse}>
        <td className="sticky left-0 bg-[var(--paper)] !pb-2">
          <div className="flex items-center gap-1">
            <button type="button" className="row-control shrink-0" tabIndex={-1}>
              {isCollapsed ? (
                <ChevronRight className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
            <span className="font-semibold text-[var(--blue)]" style={{ fontSize: '13px' }}>
              {name}
            </span>
          </div>
        </td>
        {isCollapsed ? (
          subtotals.map((v, i) => (
            <td key={monthYms[i]} className="text-right font-semibold text-[var(--blue)] !pb-2">
              {v ? fmtMoney(v) : ''}
            </td>
          ))
        ) : (
          <td colSpan={colCount - 1} />
        )}
      </tr>

      {!isCollapsed && (
        <>
          {rows.map((row) => {
            const lineId = codeToLineId.get(row.code) ?? `_xero_${row.code}`;
            const hasBudgetLink = codeToLineId.has(row.code);
            const profile = profileByLine.get(lineId) ?? null;
            const rowIsIncome = effectiveIsIncome(row);
            const byLine = (m: CashflowMonth) => (rowIsIncome ? m.incomeByLine : m.expenseByLine);
            const monthValues = projection.months.map((m) => byLine(m)[lineId] ?? 0);
            return (
              <SheetLineRow
                key={row.code}
                code={row.code}
                name={row.name}
                hasBudgetLink={hasBudgetLink}
                xeroHasGst={row.hasGst}
                projectedYms={projectedYms}
                monthValues={monthValues}
                monthYms={monthYms}
                profile={profile}
                onProfileChange={(patch) => onProfileChange(lineId, patch)}
                isRegistrar={isRegistrar}
              />
            );
          })}
          <SubtotalRow label={name} values={subtotals} monthYms={monthYms} variant="category" />
        </>
      )}
    </>
  );
}

// ─── CashflowPage ─────────────────────────────────────────────────────────────

export function CashflowPage({ budget, onBack, isViewer, isRegistrar, onApplyToBudget }: Props) {
  const [cashflow, setCashflow] = useState<CashflowData | null>(null);
  const [monthlyActuals, setMonthlyActuals] = useState<XeroMonthlyActuals | null>(null);
  const [sheetData, setSheetData] = useState<XeroCashByMonthData | null>(null);
  const [history, setHistory] = useState<CashflowData[]>([]);
  const [busy, setBusy] = useState<'load' | 'save' | null>('load');
  const [err, setErr] = useState<string | null>(null);
  const [bankPanelOpen, setBankPanelOpen] = useState(false);
  const [savedNote, setSavedNote] = useState<string>('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [fy26Preview, setFy26Preview] = useState(false);
  const [fy26Proposal, setFy26Proposal] = useState<Fy26Proposal | null>(null);
  const [fy26Applying, setFy26Applying] = useState(false);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);

  const toggleSection = useCallback((name: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const load = useCallback(() => {
    setBusy('load');
    pendingScrollRef.current = true;
    // Bank balances are read directly from the Sheet via getCashflowData.
    // The separate getBankBalances() RPC was used to attempt a Xero sync,
    // which silently no-op'd under the budget tool's Xero scope — disabled
    // until scopes can be widened (see CashflowService.gs#getBankBalances).
    Promise.all([
      api.budgetCashflowGet(),
      api.budgetCashByMonth(),
      Promise.resolve({ periods: [], rows: [] } as XeroCashByMonthData),
    ])
      .then(([d, actuals, sheet]) => {
        setCashflow(d);
        setMonthlyActuals(actuals.periods.length > 0 ? actuals : null);
        setSheetData(sheet.periods.length > 0 ? sheet : null);
        setHistory([]);
        setBusy(null);
      })
      .catch((e: Error) => {
        setErr(e.message);
        setBusy(null);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = useCallback((fn: (w: CashflowData) => CashflowData) => {
    setCashflow((current) => {
      if (!current) return current;
      const next = fn(current);
      setHistory((h) => {
        const trimmed = h.length >= HISTORY_CAP ? h.slice(-(HISTORY_CAP - 1)) : h;
        return [...trimmed, current];
      });
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1] ?? null;
      setCashflow(prev);
      return h.slice(0, -1);
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.key === 'z' || e.key === 'Z')) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.shiftKey) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  const flash = (msg: string) => {
    setSavedNote(msg);
    window.setTimeout(() => setSavedNote(''), 2200);
  };

  const handleSave = async () => {
    if (!cashflow || !isRegistrar) return;
    setBusy('save');
    setErr(null);
    try {
      const saved = await api.budgetCashflowSave(cashflow);
      setCashflow(saved);
      setHistory([]);
      flash('Saved');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const projection = useMemo(() => {
    if (!cashflow) return null;
    return computeCashflow({
      budget,
      cashflow,
      ...(monthlyActuals ? { monthlyActuals } : {}),
      ...(sheetData ? { sheetData } : {}),
    });
  }, [budget, cashflow, monthlyActuals, sheetData]);

  // ── Sheet-primary structure ──────────────────────────────────────────────────

  // GL code → budget line ID (income or expense). Used to link sheet rows to
  // the cashflow projection's incomeByLine/expenseByLine maps.
  const codeToLineId = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of budget.income) if (l.code) m.set(l.code, l.id);
    for (const l of budget.expenses) if (l.code) m.set(l.code, l.id);
    return m;
  }, [budget.income, budget.expenses]);

  // Budget income line IDs — used to override row.isIncome for budget-linked rows.
  const incomeLineIds = useMemo(() => new Set(budget.income.map((l) => l.id)), [budget.income]);

  // All sheet rows grouped by section heading, preserving Xero report order.
  const sheetSections = useMemo(() => {
    if (!sheetData) return [];
    const names: string[] = [];
    const byName = new Map<string, XeroCashByMonthRow[]>();
    for (const row of sheetData.rows) {
      let rows = byName.get(row.section);
      if (!rows) {
        rows = [];
        byName.set(row.section, rows);
        names.push(row.section);
      }
      rows.push(row);
    }
    return names.map((name) => ({ name, rows: byName.get(name) ?? [] }));
  }, [sheetData]);

  const effectiveIsIncome = useCallback(
    (row: XeroCashByMonthRow): boolean => {
      const lineId = codeToLineId.get(row.code);
      if (lineId !== undefined) return incomeLineIds.has(lineId);
      return row.isIncome;
    },
    [codeToLineId, incomeLineIds],
  );

  const incomeSections = useMemo(
    () => sheetSections.filter((s) => s.rows.some((r) => effectiveIsIncome(r))),
    [sheetSections, effectiveIsIncome],
  );

  const expenseSections = useMemo(
    () => sheetSections.filter((s) => s.rows.some((r) => !effectiveIsIncome(r))),
    [sheetSections, effectiveIsIncome],
  );

  // ── Profile management ───────────────────────────────────────────────────────

  const profileByLine = useMemo(() => {
    const m = new Map<string, TimingProfile>();
    cashflow?.timingProfiles.forEach((p) => m.set(p.lineId, p));
    return m;
  }, [cashflow]);

  // Build a lineId → Xero-hasGst lookup so newly-created profiles
  // inherit Xero's TaxType as their default rather than silently
  // overriding it with `true`.
  const xeroHasGstByLineId = useMemo(() => {
    const m = new Map<string, boolean>();
    if (!sheetData) return m;
    const codeToLine = new Map<string, string>();
    [...budget.income, ...budget.expenses].forEach((line) => {
      if (line.code) codeToLine.set(line.code, line.id);
    });
    sheetData.rows.forEach((row) => {
      const linkedId = codeToLine.get(row.code) ?? `_xero_${row.code}`;
      m.set(linkedId, row.hasGst);
    });
    return m;
  }, [sheetData, budget.income, budget.expenses]);

  const setProfileFor = useCallback(
    (lineId: string, patch: Partial<TimingProfile>) => {
      mutate((prev) => {
        const existing = prev.timingProfiles.find((p) => p.lineId === lineId);
        const xeroHasGst = xeroHasGstByLineId.get(lineId) ?? true;
        const next: TimingProfile = existing
          ? { ...existing, ...patch }
          : { ...defaultProfileFor(lineId, 0, xeroHasGst), ...patch };
        const others = prev.timingProfiles.filter((p) => p.lineId !== lineId);
        return { ...prev, timingProfiles: [...others, next] };
      });
    },
    [mutate, xeroHasGstByLineId],
  );

  const setSetting = <K extends keyof CashflowData['settings']>(
    key: K,
    value: CashflowData['settings'][K],
  ) => {
    setCashflow((prev) =>
      prev ? { ...prev, settings: { ...prev.settings, [key]: value } } : prev,
    );
  };

  // ── Per-section totals ───────────────────────────────────────────────────────

  const totalIncomeValues = useMemo(() => {
    if (!projection) return [];
    const rows = sheetSections.flatMap((s) => s.rows.filter((r) => effectiveIsIncome(r)));
    return projection.months.map((m) => {
      let sum = 0;
      for (const row of rows) {
        const lineId = codeToLineId.get(row.code) ?? `_xero_${row.code}`;
        if (profileByLine.get(lineId)?.excluded) continue;
        sum += m.incomeByLine[lineId] ?? 0;
      }
      return sum;
    });
  }, [projection, sheetSections, codeToLineId, profileByLine, effectiveIsIncome]);

  const totalExpenseValues = useMemo(() => {
    if (!projection) return [];
    const rows = sheetSections.flatMap((s) => s.rows.filter((r) => !effectiveIsIncome(r)));
    return projection.months.map((m) => {
      let sum = 0;
      for (const row of rows) {
        const lineId = codeToLineId.get(row.code) ?? `_xero_${row.code}`;
        if (profileByLine.get(lineId)?.excluded) continue;
        sum += m.expenseByLine[lineId] ?? 0;
      }
      return sum;
    });
  }, [projection, sheetSections, codeToLineId, profileByLine, effectiveIsIncome]);

  // ── FY → budget proposal ─────────────────────────────────────────────────────

  const buildFy26Proposal = useCallback(() => {
    if (!projection) return;
    const baseYear = budget.settings.baseYear;
    const fy26Months = projection.months.filter((m) => {
      const [y, mIdx] = parseYm(m.ym);
      return (mIdx >= 9 ? y + 1 : y) === baseYear;
    });
    if (fy26Months.length === 0) return;

    const totals: Record<string, number> = {};
    for (const m of fy26Months) {
      for (const [id, amt] of Object.entries(m.incomeByLine)) {
        if (!id.startsWith('_xero_')) totals[id] = (totals[id] ?? 0) + amt;
      }
      for (const [id, amt] of Object.entries(m.expenseByLine)) {
        if (!id.startsWith('_xero_')) totals[id] = (totals[id] ?? 0) + amt;
      }
    }

    const toLine = (l: LineItem): Fy26ProposalLine => ({
      id: l.id,
      code: l.code,
      name: l.name,
      current: l.currentAmount,
      proposed: Math.round(totals[l.id] ?? 0),
    });

    const firstMonth = fy26Months[0];
    const lastMonth = fy26Months[fy26Months.length - 1];
    if (!firstMonth || !lastMonth) return;
    setFy26Proposal({
      baseYear,
      firstYm: firstMonth.ym,
      lastYm: lastMonth.ym,
      income: budget.income.filter((l) => !l.deletedAt && totals[l.id] !== undefined).map(toLine),
      expenses: budget.expenses
        .filter((l) => !l.deletedAt && totals[l.id] !== undefined)
        .map(toLine),
    });
    setFy26Preview(true);
  }, [projection, budget]);

  const applyFy26 = async () => {
    if (!fy26Proposal || !onApplyToBudget) return;
    setFy26Applying(true);
    const proposedById: Record<string, number> = {};
    for (const l of [...fy26Proposal.income, ...fy26Proposal.expenses]) {
      proposedById[l.id] = l.proposed;
    }
    const updated: BudgetData = {
      ...budget,
      income: budget.income.map((l) =>
        proposedById[l.id] !== undefined ? { ...l, currentAmount: proposedById[l.id] ?? 0 } : l,
      ),
      expenses: budget.expenses.map((l) =>
        proposedById[l.id] !== undefined ? { ...l, currentAmount: proposedById[l.id] ?? 0 } : l,
      ),
    };
    try {
      await onApplyToBudget(updated);
      setFy26Preview(false);
      setFy26Proposal(null);
    } finally {
      setFy26Applying(false);
    }
  };

  // ── Scroll to last finalised month on load ───────────────────────────────────

  useEffect(() => {
    if (!projection || !pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const targetYm = cashflow?.settings.lastFinalisedMonth ?? '';
    requestAnimationFrame(() => {
      const container = tableScrollRef.current;
      if (!container) return;
      const allMonthThs = Array.from(container.querySelectorAll<HTMLElement>('thead th[data-ym]'));
      const targetTh = allMonthThs.find((th) => th.dataset.ym === targetYm);
      if (!targetTh) {
        container.scrollLeft = 0;
        return;
      }
      const colWidth = targetTh.offsetWidth;
      container.scrollLeft = targetTh.offsetLeft - container.clientWidth + colWidth * 2;
    });
  }, [projection, cashflow?.settings.lastFinalisedMonth]);

  // ── Loading / error ──────────────────────────────────────────────────────────

  if (!cashflow) {
    return (
      <div className="panel">
        <div className="panel-section py-16 text-center text-[13px] text-ccaanz-grey">
          {err ? `Error: ${err}` : 'Loading cashflow…'}
        </div>
      </div>
    );
  }

  const monthYms = projection?.months.map((m) => m.ym) ?? [];
  const colCount = monthYms.length + 1;

  return (
    <div className="space-y-3">
      {/* ── Toolbar ── */}
      <section className="panel no-print">
        <div className="panel-section flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className="btn btn-ghost btn-sm"
              title="Back to budget"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="text-[15px] font-semibold text-ccaanz-black ml-2">
              Cashflow projection
            </div>
            <div className="text-[12px] text-ccaanz-grey ml-2">
              {projection && projection.months.length > 0
                ? `${MONTH_LABEL(projection.months[0]?.ym ?? '')} – ${MONTH_LABEL(projection.months[projection.months.length - 1]?.ym ?? '')}`
                : `Rolling ${cashflow.settings.horizonMonths}-month outlook`}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {savedNote && <span className="text-[12px] text-ccaanz-grey">{savedNote}</span>}
            <button
              type="button"
              onClick={() => setBankPanelOpen(true)}
              className="btn btn-ghost btn-sm"
            >
              <Banknote className="w-3.5 h-3.5" />
              Bank accounts
              {cashflow && (
                <span className="ml-1 text-[10px] text-ccaanz-grey">
                  ({cashflow.bankAccounts.length + cashflow.termDeposits.length})
                </span>
              )}
            </button>
            {projection && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const derived = deriveBudget(budget);
                    await downloadCombinedXlsx({
                      projection,
                      sheetData,
                      budget,
                      derived,
                      baseYear: budget.settings.baseYear,
                    });
                    flash('Downloaded');
                  } catch (e) {
                    setErr((e as Error).message);
                  }
                }}
                className="btn btn-ghost btn-sm"
                title="Download Budget + Cashflow + Reconciliation as a single Excel workbook"
              >
                <Download className="w-3.5 h-3.5" />
                Download .xlsx
              </button>
            )}
            {isRegistrar && (
              <span
                className="text-[11px] text-ccaanz-grey"
                title="Bank balances are entered manually. Live Xero sync is not currently supported under the budget tool's Xero scope."
              >
                Balances: manual
              </span>
            )}
            {history.length > 0 && (
              <button
                type="button"
                onClick={undo}
                className="btn btn-ghost btn-sm"
                title="Undo last change (⌘Z)"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Undo
              </button>
            )}
            <button
              type="button"
              onClick={load}
              className="btn btn-ghost btn-sm"
              disabled={busy !== null}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {busy === 'load' ? 'Loading…' : 'Refresh'}
            </button>
            {isRegistrar && onApplyToBudget && projection && (
              <button
                type="button"
                onClick={buildFy26Proposal}
                disabled={busy !== null}
                className="btn btn-ghost btn-sm"
                title={`Import FY${budget.settings.baseYear} actuals + projections into the budget`}
              >
                <ArrowDownToLine className="w-3.5 h-3.5" />
                FY{budget.settings.baseYear} → budget
              </button>
            )}
            {isRegistrar && (
              <button
                type="button"
                onClick={handleSave}
                disabled={busy !== null}
                className="btn btn-primary btn-sm"
              >
                <Save className="w-3.5 h-3.5" />
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </section>

      {err && (
        <section className="panel">
          <div className="panel-section text-[12px] text-ccaanz-red">{err}</div>
        </section>
      )}

      {/* ── FY → Budget preview ── */}
      {fy26Preview && fy26Proposal && isRegistrar && (
        <section className="panel border-2 border-amber-300">
          <div className="panel-section">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="text-[13px] font-semibold text-ccaanz-black">
                  FY{fy26Proposal.baseYear} cashflow → budget proposal
                </div>
                <div className="text-[11px] text-ccaanz-grey mt-0.5">
                  {MONTH_LABEL(fy26Proposal.firstYm)} – {MONTH_LABEL(fy26Proposal.lastYm)} actuals +
                  projections, summed per line. Amounts are GST-exclusive. Review for reasonableness
                  before applying.
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setFy26Preview(false);
                    setFy26Proposal(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={applyFy26}
                  disabled={fy26Applying || !onApplyToBudget}
                >
                  {fy26Applying ? 'Applying…' : 'Apply to budget'}
                </button>
              </div>
            </div>
            {[
              { label: 'INCOME', lines: fy26Proposal.income },
              { label: 'EXPENSES', lines: fy26Proposal.expenses },
            ].map(({ label, lines }) =>
              lines.length === 0 ? null : (
                <div key={label} className="mb-4">
                  <div className="text-[10px] font-semibold text-ccaanz-grey tracking-wider mb-1">
                    {label}
                  </div>
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-ccaanz-grey border-b border-ccaanz-rule">
                        <th className="text-left py-1 pr-3 font-medium w-12">Code</th>
                        <th className="text-left py-1 pr-3 font-medium">Line</th>
                        <th className="text-right py-1 pr-3 font-medium w-28">Current budget</th>
                        <th className="text-right py-1 pr-3 font-medium w-28">Proposed</th>
                        <th className="text-right py-1 font-medium w-28">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const diff = l.proposed - l.current;
                        return (
                          <tr key={l.id} className="border-b border-ccaanz-rule/50">
                            <td className="py-1 pr-3 text-ccaanz-grey">{l.code}</td>
                            <td className="py-1 pr-3">{l.name}</td>
                            <td className="py-1 pr-3 text-right tabular-nums">
                              {fmtMoney(l.current)}
                            </td>
                            <td className="py-1 pr-3 text-right tabular-nums font-medium">
                              {fmtMoney(l.proposed)}
                            </td>
                            <td
                              className={`py-1 text-right tabular-nums ${diff > 0 ? 'text-green-700' : diff < 0 ? 'text-ccaanz-red' : 'text-ccaanz-grey'}`}
                            >
                              {diff > 0 ? '+' : ''}
                              {fmtMoney(diff)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ),
            )}
          </div>
        </section>
      )}

      {/* ── Alerts ── */}
      {projection && projection.alerts.length > 0 && (
        <section className="panel">
          <div className="panel-section">
            <div className="panel-section-title flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-ccaanz-red" />
              Alerts ({projection.alerts.length})
            </div>
            <ul className="mt-2 space-y-1 text-[12px]">
              {projection.alerts.map((a, i) => (
                <li key={`${a.kind}-${i}`} className="flex items-start gap-2">
                  <span className="text-ccaanz-grey w-16">{MONTH_LABEL(a.ym)}</span>
                  <span>{describeAlert(a)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ── Settings ── */}
      <section className="panel">
        <div className="panel-section">
          <div className="panel-section-title flex items-center gap-2">
            <SettingsIcon className="w-3.5 h-3.5" />
            Cashflow settings
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
            <label className="flex flex-col gap-1">
              <span className="text-ccaanz-grey">Last finalised month</span>
              <input
                type="month"
                className="input"
                value={cashflow.settings.lastFinalisedMonth}
                disabled={!isRegistrar}
                onChange={(e) => setSetting('lastFinalisedMonth', e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-ccaanz-grey">Horizon (months)</span>
              <input
                type="number"
                min={1}
                max={36}
                className="input num"
                value={cashflow.settings.horizonMonths}
                disabled={!isRegistrar}
                onChange={(e) => setSetting('horizonMonths', parseInt(e.target.value, 10) || 12)}
              />
            </label>
          </div>
        </div>
      </section>

      {/* ── Monthly grid ── */}
      {projection && (
        <section className="panel">
          {/* Title sits OUTSIDE the scroll container so that sticky top-0
              on the <th> cells pins to the very top of the scrollable area.
              When the title was inside the scroll div, data rows could appear
              above the header because top:0 was measured from a div that
              included title height + padding. */}
          <div className="panel-section">
            <div className="panel-section-title flex items-center gap-2">
              <Coins className="w-3.5 h-3.5" />
              Monthly cashflow
              {isRegistrar && (
                <span className="text-[10px] text-ccaanz-grey font-normal ml-1">
                  — click a value to override · <Eye className="w-2.5 h-2.5 inline" /> to exclude
                </span>
              )}
            </div>

            {/* Scroll container wraps the table only — no title above the
                sticky header row inside this div. */}
            <div
              className="overflow-auto scrollbar-hidden"
              ref={tableScrollRef}
              style={{ maxHeight: '72vh' }}
            >
              {sheetData ? (
                <table className="budget-table min-w-full" style={{ fontSize: '11px' }}>
                  <thead>
                    <tr>
                      {/* Top-left corner: pinned both vertically and horizontally,
                        highest z so it covers other sticky cells when scrolling
                        in both directions. Solid background + bottom shadow so
                        nothing bleeds through when scrolled. */}
                      <th
                        className="sticky left-0 top-0 z-[3] bg-[var(--white)]"
                        style={{ minWidth: 220, boxShadow: '0 1px 0 var(--border-soft)' }}
                      >
                        Line
                      </th>
                      {projection.months.map((m) => (
                        <th
                          key={m.ym}
                          data-ym={m.ym}
                          className={`right whitespace-nowrap sticky top-0 z-[2] bg-[var(--white)] ${m.status === 'actual' ? 'col-actual' : ''}`}
                          style={{ boxShadow: '0 1px 0 var(--border-soft)' }}
                        >
                          {MONTH_LABEL(m.ym)}
                          {m.status === 'actual' ? (
                            <div className="text-[8px] font-normal tracking-wide text-[var(--brand)] opacity-70 uppercase">
                              actual
                            </div>
                          ) : (
                            <div className="text-[8px] font-normal tracking-wide text-ccaanz-grey opacity-50 uppercase">
                              forecast
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening balance */}
                    <tr className="row-data">
                      <td className="sticky left-0 bg-[var(--white)] font-medium">
                        Opening operating
                      </td>
                      {projection.months.map((m) => (
                        <td key={m.ym} className="text-right">
                          {fmtMoney(m.openingOperating)}
                        </td>
                      ))}
                    </tr>

                    {/* ── Income sections ── */}
                    <tr className="row-subhead">
                      <td colSpan={colCount}>
                        <span className="label">Income</span>
                      </td>
                    </tr>

                    {incomeSections.map((section) => (
                      <SheetSection
                        key={section.name}
                        name={section.name}
                        rows={section.rows.filter((r) => effectiveIsIncome(r))}
                        monthYms={monthYms}
                        projection={projection}
                        codeToLineId={codeToLineId}
                        incomeLineIds={incomeLineIds}
                        profileByLine={profileByLine}
                        onProfileChange={setProfileFor}
                        isRegistrar={isRegistrar}
                        isCollapsed={collapsedSections.has(section.name)}
                        onToggleCollapse={() => toggleSection(section.name)}
                      />
                    ))}

                    <SubtotalRow
                      label="Total income"
                      values={totalIncomeValues}
                      monthYms={monthYms}
                      variant="section"
                    />

                    {/* ── Expense sections ── */}
                    <tr className="row-subhead">
                      <td colSpan={colCount}>
                        <span className="label">Expenses</span>
                      </td>
                    </tr>

                    {expenseSections.map((section) => (
                      <SheetSection
                        key={section.name}
                        name={section.name}
                        rows={section.rows.filter((r) => !effectiveIsIncome(r))}
                        monthYms={monthYms}
                        projection={projection}
                        codeToLineId={codeToLineId}
                        incomeLineIds={incomeLineIds}
                        profileByLine={profileByLine}
                        onProfileChange={setProfileFor}
                        isRegistrar={isRegistrar}
                        isCollapsed={collapsedSections.has(section.name)}
                        onToggleCollapse={() => toggleSection(section.name)}
                      />
                    ))}

                    <SubtotalRow
                      label="Total expenses"
                      values={totalExpenseValues}
                      monthYms={monthYms}
                      variant="section"
                    />

                    {/* ── Summary rows ── */}
                    <tr className="row-data">
                      <td className="sticky left-0 bg-[var(--white)] font-medium">
                        GST Refund (Payment)
                      </td>
                      {projection.months.map((m) => {
                        // NZ GST pay-months are Mar/May/Jul/Sep/Nov/Jan.
                        const monthIdx = parseInt(m.ym.split('-')[1] ?? '0', 10);
                        const isPayMonth = [1, 3, 5, 7, 9, 11].includes(monthIdx);
                        if (m.gstNet) {
                          return (
                            <td key={m.ym} className="text-right">
                              {fmtMoney(-m.gstNet)}
                            </td>
                          );
                        }
                        return (
                          <td
                            key={m.ym}
                            className="text-right text-ccaanz-grey/40"
                            title={
                              isPayMonth
                                ? 'Pay-month — calc returned 0 (no GST-applicable activity)'
                                : 'Not a pay-month'
                            }
                          >
                            {isPayMonth ? '$0' : '—'}
                          </td>
                        );
                      })}
                    </tr>

                    <tr className="row-data">
                      <td className="sticky left-0 bg-[var(--white)] font-medium">
                        TD maturity in
                      </td>
                      {projection.months.map((m) => (
                        <td key={m.ym} className="text-right text-[var(--blue)]">
                          {m.tdMaturityIn ? fmtMoney(m.tdMaturityIn) : ''}
                        </td>
                      ))}
                    </tr>

                    <tr className="row-data">
                      <td className="sticky left-0 bg-[var(--white)] font-medium">
                        Waterfall drawdowns
                      </td>
                      {projection.months.map((m) => {
                        const total = m.drawdowns.reduce((s, d) => s + d.amount, 0);
                        return (
                          <td key={m.ym} className="text-right text-[var(--red)]">
                            {total ? fmtMoney(total) : ''}
                          </td>
                        );
                      })}
                    </tr>

                    <tr className="row-net">
                      <td className="sticky left-0 bg-[var(--blue-lt)]">
                        <span className="net-label">Closing operating</span>
                      </td>
                      {projection.months.map((m) => (
                        <td
                          key={m.ym}
                          className={`text-right net-value ${m.closingOperating < 0 ? 'neg' : 'pos'}`}
                        >
                          {fmtMoney(m.closingOperating)}
                        </td>
                      ))}
                    </tr>

                    <tr className="row-subtotal">
                      <td className="sticky left-0 bg-[var(--paper)]">Total liquid</td>
                      {projection.months.map((m) => (
                        <td key={m.ym} className="text-right">
                          {fmtMoney(m.closingTotalLiquid)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="mt-4 py-8 text-center text-[12px] text-ccaanz-grey">
                  No XeroCashByMonth data loaded. Upload the Xero cash report to the XeroCashByMonth
                  sheet tab to see the full cashflow grid.
                </div>
              )}
            </div>
            {/* end scroll container */}
          </div>
          {/* end panel-section */}
        </section>
      )}

      {bankPanelOpen && (
        <CashflowBankPanel
          bankAccounts={cashflow.bankAccounts}
          termDeposits={cashflow.termDeposits}
          onSave={async (next) => {
            const updated: CashflowData = {
              ...cashflow,
              bankAccounts: next.bankAccounts,
              termDeposits: next.termDeposits,
            };
            const saved = await api.budgetCashflowSave(updated);
            setCashflow(saved);
            flash('Bank accounts saved');
          }}
          onClose={() => setBankPanelOpen(false)}
          isViewer={isViewer ?? false}
        />
      )}
    </div>
  );
}

// ─── Alert descriptions ───────────────────────────────────────────────────────

function describeAlert(a: CashflowAlert): string {
  switch (a.kind) {
    case 'first-negative':
      return `First negative operating month — deficit ${fmtMoney(a.deficit)}`;
    case 'td-broken-early':
      return `TD ${a.tdReference} broken early — est. lost interest ${fmtMoney(a.lostInterest)}`;
    case 'gst-due':
      return `GST ${a.amount >= 0 ? 'payment' : 'refund'} ${fmtMoney(Math.abs(a.amount))} (period end ${a.periodEnd})`;
    case 'no-coverage':
      return `Waterfall exhausted — uncovered deficit ${fmtMoney(a.deficit)}`;
  }
}
